import { useEffect, useState } from "react";

import {
  handleHasUnsavedChanges,
  handleUpdateActivitiesSelection,
} from "@/assignments/utils/assignmentActions";
import { AttachmentWrapper } from "@/types/models/ContentAttachment.interface";
import useReadingSkillsContext from "@/view/utils/useReadingSkillsContext/useReadingSkillsContext";

interface HandleUpdateVisibleActivitiesProps {
  id?: string | undefined;
  updateType: "add" | "remove";
  visibleActivityIds: string[];
  setVisibleActivityIds: (newState: Array<string | undefined>) => void;
}

export interface UseAssignmentActivitySelectionUpdateProps {
  attachmentId?: string | undefined;
  dispatch: () => void;
  isElaSkillsQuiz: boolean;
  isSelected: boolean;
  setIsPollSelected?: ((newState: boolean) => void) | undefined;
  setVisibleActivityIds: (newState: Array<string | undefined>) => void;
  toggleChecksForUnderstanding?: ((newState: boolean) => void) | undefined;
  toggleGuidedHighlighting?: ((newState: boolean) => void) | undefined;
  toggleGraphicOrganizers?: ((newState: boolean) => void) | undefined;
  visibleActivityIds: string[];
}

const useAssignmentActivitySelectionUpdate = ({
  attachmentId,
  dispatch,
  isElaSkillsQuiz,
  isSelected,
  toggleChecksForUnderstanding,
  toggleGuidedHighlighting,
  toggleGraphicOrganizers,
  setIsPollSelected,
  setVisibleActivityIds,
  visibleActivityIds,
}: UseAssignmentActivitySelectionUpdateProps) => {
  const { allElaLanguagedQuizzes } = useReadingSkillsContext();
  const allElaQuizzesIds = allElaLanguagedQuizzes?.map(
    (c: AttachmentWrapper) => c.id,
  );
  const [isChecked, setIsChecked] = useState(isSelected);

  const { handleQuizActivitySelection } = useReadingSkillsContext();

  const handleUpdateVisibleActivities = ({
    id,
    setVisibleActivityIds,
    updateType,
    visibleActivityIds,
  }: HandleUpdateVisibleActivitiesProps) => {
    if (updateType === "remove") {
      // Ensure we are accounting for all potential languaged quizzes when dealing with ela custom quizzes.
      // When removing one languaged ELA quiz, we should remove its counterpart in other languages.
      const newVisibleActivityIds = isElaSkillsQuiz
        ? visibleActivityIds?.filter(
            (activityId) => !allElaQuizzesIds?.includes(activityId),
          )
        : visibleActivityIds.filter((activityId) => activityId !== id);

      setVisibleActivityIds(newVisibleActivityIds);
    } else {
      isElaSkillsQuiz && allElaQuizzesIds
        ? setVisibleActivityIds([
            ...new Set([...visibleActivityIds, ...allElaQuizzesIds]),
          ])
        : setVisibleActivityIds([...visibleActivityIds, id]);
    }
  };

  const onClick = () => {
    handleHasUnsavedChanges(true, dispatch);
    // Polls have their own separate functionality than other activities.
    if (setIsPollSelected) {
      setIsChecked(!isChecked);
      return setIsPollSelected(!isSelected);
    }

    if (toggleChecksForUnderstanding) {
      return toggleChecksForUnderstanding(!isChecked);
    }

    if (toggleGuidedHighlighting) {
      return toggleGuidedHighlighting(!isChecked);
    }

    if (toggleGraphicOrganizers) {
      return toggleGraphicOrganizers(!isChecked);
    }

    const action = isChecked ? "remove" : "add";
    const activityUpdateAction = { attachmentIds: [attachmentId], action };
    handleUpdateActivitiesSelection(activityUpdateAction, dispatch);
    handleUpdateVisibleActivities({
      id: attachmentId,
      setVisibleActivityIds,
      updateType: action,
      visibleActivityIds,
    });
    setIsChecked(!isChecked);

    if (isElaSkillsQuiz && handleQuizActivitySelection) {
      handleQuizActivitySelection(action);
    }
  };

  useEffect(() => {
    setIsChecked(isSelected);
  }, [isSelected]);

  return { isChecked, onClick };
};

export default useAssignmentActivitySelectionUpdate;
